import { useState } from "react";
import { LogOut, Monitor, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { useApi } from "../../lib/useApi";
import { api, formatApiError, COMPANY_KEY } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Loader, ErrorState } from "../common/States";
import { ConfirmButton } from "../common/ConfirmButton";

const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
const device = (ua) => (ua || "Appareil inconnu").replace(/\(.*?\)/g, "").slice(0, 80);

export default function SessionsCard() {
  const { data, loading, error, reload } = useApi("/auth/sessions");
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const revoke = async (id) => {
    try { await api.delete(`/auth/sessions/${id}`); toast.success("Session fermée"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const logoutAll = async () => {
    setBusy(true);
    try { await api.post("/auth/logout-all"); localStorage.removeItem(COMPANY_KEY); toast.success("Tous les appareils ont été déconnectés"); await refresh(); }
    catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };
  return (
    <div className="card-surface space-y-4 p-6" data-testid="sessions-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><p className="flex items-center gap-2 font-display font-semibold"><ShieldCheck className="h-4 w-4 text-blue-600" />Sessions actives</p>
          <p className="mt-1 text-sm text-slate-500">Une session expire après 30 minutes d'inactivité et au plus tard 7 jours après la connexion.</p></div>
        <ConfirmButton testId="sessions-logout-all" title="Déconnecter tous les appareils ?" description="Toutes vos sessions, y compris celle-ci, seront fermées immédiatement."
          confirmLabel="Tout déconnecter" onConfirm={logoutAll}
          trigger={<Button variant="outline" size="sm" disabled={busy} className="text-rose-600" data-testid="sessions-logout-all"><LogOut className="mr-1.5 h-3.5 w-3.5" />Déconnecter tous les appareils</Button>} />
      </div>
      {loading && <Loader />}
      {error && <ErrorState message={error} onRetry={reload} />}
      <ul className="divide-y divide-slate-100">
        {data?.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-3 py-2.5 text-sm" data-testid={`session-${s.id}`}>
            <div className="flex min-w-0 items-start gap-2"><Monitor className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <div className="min-w-0"><p className="truncate">{device(s.user_agent)}{s.current && <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700" data-testid="session-current">Cet appareil</span>}</p>
                <p className="text-xs text-slate-500">Ouverte le {fmt(s.created_at)} · active le {fmt(s.last_seen_at)}{s.mfa ? " · vérifiée par code" : ""}</p></div></div>
            {!s.current && <Button variant="ghost" size="sm" onClick={() => revoke(s.id)} data-testid={`session-revoke-${s.id}`}>Fermer</Button>}
          </li>
        ))}
      </ul>
    </div>
  );
}
